import React, { useState } from 'react';
import { Snackbar } from '@shared/components/Snackbar';
import { SHARED_MESSAGES } from '@/shared/constants/messages';
import { VIEW_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import type { ViewServiceRequestDetail } from '../../types/viewServiceRequestType';
import { FileRow, SectionCard } from './viewServiceRequestUI';
import { downloadFile } from './attachmentFile';

interface SubLabelProps {
  text: string;
}

const SubLabel: React.FC<SubLabelProps> = ({ text }) => (
  <p className="text-bodySmall font-normal text-on-surface-variant">{text}</p>
);

interface SRDetailsAttachmentsTabProps {
  detail: ViewServiceRequestDetail | undefined;
}

const SRDetailsAttachmentsTab: React.FC<SRDetailsAttachmentsTabProps> = ({
  detail,
}) => {
  const images = detail?.images ?? [];
  const document = detail?.document ?? null;
  const description = detail?.description?.trim() ?? '';
  const [showDownloadSnackbar, setShowDownloadSnackbar] = useState(false);

  const handleDownload = (url: string, filename: string): void => {
    setShowDownloadSnackbar(true);
    downloadFile(url, filename);
  };

  return (
    <>
      <SectionCard
        title={L.NAV_DETAILS}
        testId="view-sr-section-details-attachments"
      >
        <div className="flex flex-col gap-2">
          <SubLabel text={L.SECTION_DESCRIPTION} />
          <p
            className="text-sm text-[#181A1B] whitespace-pre-wrap break-words"
            data-testid="view-sr-description-value"
          >
            {description.length > 0 ? description : L.EMPTY_CELL}
          </p>
        </div>

        {images.length > 0 && (
          <div className="flex flex-col gap-3">
            <SubLabel text={L.SECTION_IMAGES} />
            <div className="flex flex-col gap-4">
              {images.map((img, idx) => (
                <FileRow
                  key={`${img.filename}-${idx}`}
                  file={img}
                  testId={`view-sr-image-${idx}`}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          </div>
        )}

        {document && (
          <div className="flex flex-col gap-3">
            <SubLabel text={L.SECTION_DOCUMENT} />
            <FileRow
              file={document}
              testId="view-sr-document"
              onDownload={handleDownload}
            />
          </div>
        )}
      </SectionCard>

      <Snackbar
        open={showDownloadSnackbar}
        message={SHARED_MESSAGES.DOWNLOAD_READY_MESSAGE}
        variant="success"
        onClose={() => setShowDownloadSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};

export default SRDetailsAttachmentsTab;
